/**
 * paymentService — Couche d'abstraction unique pour TOUS les paiements.
 *
 * Routage automatique selon la plateforme :
 *   - Capacitor iOS natif  → Apple IAP via RevenueCat
 *   - Capacitor Android    → Google Play Billing via RevenueCat (futur)
 *   - Tout le reste (web)  → Stripe (existant, inchangé)
 *
 * Cette couche encapsule TOUTE la logique de paiement. Les composants UI
 * (Pricing, SubscriptionGate, etc.) NE doivent JAMAIS appeler Stripe ou
 * RevenueCat directement — ils passent toujours par ce service.
 *
 * RevenueCat est chargé en `import dynamique` pour qu'il n'apparaisse
 * JAMAIS dans le bundle web. Sur web, on ne charge que Stripe.
 */

import { isCapacitorIOS, isCapacitorNative } from './platform';
import { applePlanIdFor } from './revenueCatProducts';
import { billingApi } from './billingApi';

// Cache du module RevenueCat chargé dynamiquement (1 fois, sur natif uniquement).
let _rcModule = null;
let _rcConfigured = false;

async function loadRevenueCat() {
  if (_rcModule) return _rcModule;
  // eslint-disable-next-line no-undef -- Webpack dynamic import
  const mod = await import('@revenuecat/purchases-capacitor');
  _rcModule = mod;
  return mod;
}

/**
 * Configure le SDK RevenueCat avec l'identité Supabase de l'utilisateur.
 * À appeler une seule fois après la connexion, et chaque fois qu'on change
 * d'utilisateur. No-op sur le web.
 */
export async function initBilling({ userId } = {}) {
  if (!isCapacitorNative()) return; // web → rien à faire
  const apiKey = process.env.REACT_APP_REVENUECAT_IOS_PUBLIC_KEY;
  if (!apiKey) {
    console.warn('[paymentService] REACT_APP_REVENUECAT_IOS_PUBLIC_KEY manquante — RevenueCat désactivé');
    return;
  }
  try {
    const { Purchases, LOG_LEVEL } = await loadRevenueCat();
    if (!_rcConfigured) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
      await Purchases.configure({ apiKey, appUserID: userId || null });
      _rcConfigured = true;
    }
    if (userId) {
      await Purchases.logIn({ appUserID: userId });
    }
  } catch (err) {
    console.error('[paymentService] initBilling failed:', err);
  }
}

/** Déconnecte RevenueCat (à appeler dans signOut). No-op sur le web. */
export async function logoutBilling() {
  if (!isCapacitorNative() || !_rcConfigured) return;
  try {
    const { Purchases } = await loadRevenueCat();
    await Purchases.logOut();
  } catch (err) {
    console.error('[paymentService] logoutBilling failed:', err);
  }
}

/**
 * Récupère les offerings RevenueCat (produits configurés dans le dashboard
 * RevenueCat). Retourne `null` sur le web ou si rien n'est configuré.
 */
export async function getNativeOfferings() {
  if (!isCapacitorNative()) return null;
  try {
    const { Purchases } = await loadRevenueCat();
    const { current } = await Purchases.getOfferings();
    return current || null;
  } catch (err) {
    console.error('[paymentService] getNativeOfferings failed:', err);
    return null;
  }
}

/**
 * Démarre un achat — point d'entrée UNIVERSEL pour les composants UI.
 *
 * @param {object} args
 * @param {string} args.planKey - Clé interne ('weekly_solo', 'monthly_solo', ...)
 * @returns {Promise<{ok: boolean, redirected?: boolean, error?: string}>}
 *
 * Sur iOS natif : déclenche la modale Apple IAP, retourne quand l'achat est
 *   confirmé/refusé. Côté backend, RevenueCat enverra un webhook qui mettra
 *   à jour `subscriptions` dans Supabase.
 * Sur web : redirige vers Stripe Checkout (la promesse ne résout pas car
 *   `window.location.href` change la page).
 */
export async function startCheckout({ planKey }) {
  if (isCapacitorIOS()) {
    return startAppleIAP(planKey);
  }
  // Tous les autres mondes → Stripe
  const { url } = await billingApi.createCheckout(planKey);
  window.location.href = url;
  return { ok: true, redirected: true };
}

async function startAppleIAP(planKey) {
  const productId = applePlanIdFor(planKey);
  if (!productId) {
    return { ok: false, error: `Plan inconnu pour Apple IAP: ${planKey}` };
  }
  try {
    const { Purchases } = await loadRevenueCat();
    const { current } = await Purchases.getOfferings();
    if (!current?.availablePackages?.length) {
      return { ok: false, error: 'Aucun produit RevenueCat configuré' };
    }
    const pkg = current.availablePackages.find(
      (p) => p.product?.identifier === productId
    );
    if (!pkg) {
      return { ok: false, error: `Produit Apple non trouvé: ${productId}` };
    }
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    return { ok: true, customerInfo: result.customerInfo };
  } catch (err) {
    // Apple annule = userCancelled, on ne traite pas ça comme une erreur.
    if (err?.userCancelled) {
      return { ok: false, userCancelled: true };
    }
    return { ok: false, error: err?.message || String(err) };
  }
}

/**
 * Restaure les achats précédents (obligatoire pour conformité Apple).
 * Affiche un toast de succès et force un rafraîchissement de `billingApi.status()`.
 */
export async function restorePurchases() {
  if (!isCapacitorNative()) {
    return { ok: false, error: 'Restauration disponible uniquement sur l\'app native' };
  }
  try {
    const { Purchases } = await loadRevenueCat();
    const result = await Purchases.restorePurchases();
    return { ok: true, customerInfo: result.customerInfo };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
}

/** Helper sémantique pour les composants UI. */
export function isNativePaymentEnvironment() {
  return isCapacitorNative();
}
